import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild } from '@angular/core';
import { ValidateService } from 'src/app/core/validate.service';
import { BlendEvent, Constants, ErrCode, ErrorCode, FileItem, PathAddApiOutput } from 'src/app/shared/models';
import { FileListService } from '../services/file-list.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { Subscription } from 'rxjs';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { TextFieldComponent } from '@material-library';
import { BlendService } from 'src/app/shared/services/blend.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-dialog-new-folder',
  templateUrl: './dialog-new-folder.component.html',
  styleUrls: ['./dialog-new-folder.component.scss']
})
export class DialogNewFolderComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('folderNameInput') folderNameInput: TextFieldComponent;
  @Output() showDialogChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() folderCreated: EventEmitter<FileItem> = new EventEmitter<FileItem>();
  @Input() showDialog!: boolean;
  @Input() syncPid!: number;
  @Input() public folderType: 'folder' | 'shared-folder' = 'folder';
  @Input() showFolderType = true;
  @Input() public closeIconName: "close" | "arrow-left" = 'close';
  @Input() public isSharedFolder?: boolean = false;
  @Input() selectedPathListData: FileItem[];
  public errors: Array<ErrorCode> = [];
  public folderName: string = '';
  public errorMessage: string = '';
  public isLoading: boolean = false;
  private pathlist: FileItem[] = [];
  public errcode: ErrCode;
  public openShareFolderDialog: boolean = false;
  public newFolder: FileItem[];
  private fileListSubscription: Subscription;
  private breakpointSubscribe: Subscription;

  private creating: boolean;
  showEmailVerifyDialog:boolean = false;
  public isSmallScreen: boolean = false;

  constructor(
    private fileListService: FileListService,
    private validateService: ValidateService,
    private snackbarService: SnackbarService,
    private loggerService: LoggerService,
    private userService: UserService,
    public breakpointObserver: BreakpointObserver,
    private blendService: BlendService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.breakpointSubscribe = this.breakpointObserver.observe([
      '(max-width: 599px)',
    ])
    .subscribe((state: BreakpointState) => {
      if (state.breakpoints['(max-width: 599px)']) {
        this.isSmallScreen = true;
      } else {
        this.isSmallScreen = false;
      }
      
    }); 
    if (this.selectedPathListData === undefined) {
      this.fileListSubscription = this.fileListService.getListSubscription().subscribe((data) => {
        if (data && data.pathlist.length) {
          this.pathlist = data.pathlist;
        }
      });
    } else {
      this.pathlist = this.selectedPathListData;
    }
  }

  ngAfterViewInit(): void {
    this.focusInput();
  }

  ngAfterViewChecked(): void {
    setTimeout(() => {
      this.focusInput();
    }, 0);
  }

  focusInput() {
    const input = this.folderNameInput?.input as unknown as QueryList<ElementRef<HTMLInputElement>>;
    if (input) {
      input.first.nativeElement.focus();
    }
  }  

  
  ngOnDestroy(): void {
    if (this.fileListSubscription) {
      this.fileListSubscription.unsubscribe();
    }
    if (this.breakpointSubscribe) {
      this.breakpointSubscribe.unsubscribe();
    }
  }

  closeDialog = (created=false) => {
    if (!this.openShareFolderDialog || created) {
      this.showDialog = false;
      setTimeout(() => {
        if (!created && !this.creating) {
          this.folderCreated.emit(null);
        }
        this.showDialogChange.next(false);
      }, 0);
    }
  }

  onSelect = (folderType: 'folder' | 'shared-folder') => {
    this.showEmailVerifyDialog = false;
    if (folderType === 'shared-folder' && !this.userService.get('is_verified')) {
      this.showEmailVerifyDialog = true;
      return;
    }
    this.folderType = folderType;
  }

  folderNameChange = (name: string) => {
    this.errorMessage = ''
    this.errcode = null;
    this.errors = [];
  }

  createFolder = async () => {
    try {
      if (!this.isValid()) {
        return;
      }
      if (!this.syncPid) {
        const routeSyncId = this.route.snapshot.params['sync_id'];
        this.syncPid = parseInt(routeSyncId, 10);
      }
      if (this.folderType === 'shared-folder') {
        this.openShareFolderDialog = true;
      }
      else {
        this.creating = true;
        this.isLoading = true;
        const result: PathAddApiOutput = await this.fileListService.mkdir(this.syncPid, this.folderName);
        if (result) {
          result.pathitem.name = this.folderName;
          this.folderCreated.emit(result.pathitem);
          this.snackbarService.showMessage('success', `'${this.folderName}' was successfully created.`);
          this.closeDialog(true);
        }
        else {
          this.snackbarService.showMessage('critical', `Couldn't complete action. Try again.`);
          this.isLoading = false;
        }
      }
    } catch (err) {
      this.errors.push({ code: err.code });
      this.errcode = ErrCode.fromException(err);
      if (!err.code || (err.code !== 7245 && err.code !== 7246)) {
        this.snackbarService.showMessage("critical", `Couldn't complete action. Try again.`);
      }
      this.loggerService.e('Error in create folder ', this.errcode);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * check if folder name is valid
   * @returns boolean true/false
   */
  public isValid(): boolean {
    this.blendService.track(BlendEvent.BUTTON_CLICKED, { button_text: 'Continue (creating share)'});
    if (!this.folderName) {
      this.errorMessage = 'Enter a valid folder name.';
      return false;
    }
    if (this.validateService.hasLeadingSpace(this.folderName)) {
      this.errorMessage = 'Files cannot begin with a space.';
      return false;
    }
    if (this.validateService.hasTrailingChar(this.folderName)) {
      this.errorMessage = 'Files cannot end in a . or a space.';
      return false;
    }
    if (this.validateService.hasReservedNames(this.folderName)) {
      this.errorMessage = 'This name is a reserved and cannot be used.';
      return false;
    }
    if (this.validateService.hasIllegalCharacters(this.folderName)) {
      this.errorMessage = `Enter a valid folder name.`;
      throw { code: 7246 };
    }
    if (this.validateService.isExist(this.folderName, this.pathlist)) {
      this.errorMessage = 'Enter a valid folder name.';
      throw { code: 7245 };
    }
    if (this.folderName.length > Constants.MAX_FILE_NAME_LEN) {
      this.errorMessage = `The length of the folder name should not be greater than ${Constants.MAX_FILE_NAME_LEN}`;
      return false;
    }
    this.errorMessage = '';
    return true;
  }
  public closeEmailVerifyDialog() {
      this.showEmailVerifyDialog = false;
  }

  onShareFolderCreateSuccess = (success: boolean) => {
    if (success) {
      this.closeDialog(true);
    }
  }
  public learnMoreClick = () => {
    window.open('https://www.sync.com/help/how-to-use-team-shared-folders/#shareinshare', '_blank');
  }
}
